import { useContext, useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import * as stylex from "@stylexjs/stylex";
import { DSPDisplayModeContext } from "@x1-contexts/DSPDisplayModeContext";

import defaultProfile from "../assets/images/default-profile.jpg";
import x1Black from "../assets/images/x1-black.jpg";
import x1White from "../assets/images/x1-white.jpg";
import { PROJECTS, TECH_STACKS } from "../data/placeholder";

function Home() {
  const themeDisplayMode = useContext(DSPDisplayModeContext);
  const isDark = themeDisplayMode === "dark";
  const profileImage = isDark ? x1White : x1Black;
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isRevealed, setIsRevealed] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (isHovered) {
      timerRef.current = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 100) {
            clearInterval(timerRef.current);
            setIsRevealed((prevRevealed) => !prevRevealed);
            return 100;
          }
          return prev + 2;
        });
      }, 30);
    } else {
      clearInterval(timerRef.current);
      setProgress(0);
    }
    return () => clearInterval(timerRef.current);
  }, [isHovered]);

  return (
    <div {...stylex.props(styles.pageWrapper)}>
      <div {...stylex.props(styles.bentoGrid)}>
        <div {...stylex.props(styles.cell, styles.heroCell)}>
          {loading && (
            <div {...stylex.props(styles.skeleton)}>
              <svg viewBox="25 25 50 50" {...stylex.props(styles.loaderSvg)}>
                <circle
                  cx="50"
                  cy="50"
                  r="24"
                  {...stylex.props(styles.loaderCircle)}
                />
              </svg>
            </div>
          )}
          <div
            {...stylex.props(styles.profileRevealContainer)}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <div
              {...stylex.props(
                styles.imageWrapper,
                isRevealed && styles.isFlipped,
              )}
            >
              <img
                src={profileImage}
                alt="Profile"
                {...stylex.props(styles.profileImage, styles.frontImage)}
                style={{ display: loading ? "none" : "block" }}
                onLoad={() => setLoading(false)}
              />
              <img
                src={defaultProfile}
                alt="Revealed Profile"
                {...stylex.props(styles.profileImage, styles.backImage)}
              />
            </div>
            {isHovered && progress < 100 && (
              <svg viewBox="0 0 150 150" {...stylex.props(styles.progressSvg)}>
                <circle
                  cx="75"
                  cy="75"
                  r="72"
                  {...stylex.props(styles.progressCircleBack)}
                />
                <circle
                  cx="75"
                  cy="75"
                  r="72"
                  {...stylex.props(styles.progressCircleFront)}
                  style={{
                    strokeDashoffset: 452 - (progress / 100) * 452,
                  }}
                />
              </svg>
            )}
          </div>
          <h1 {...stylex.props(styles.heroTitle)}>Alexis Dela Cruz</h1>
          <p {...stylex.props(styles.heroSubtitle)}>
            Crafting digital experiences with code & creativity.
          </p>
        </div>

        <div {...stylex.props(styles.cell, styles.aboutCell)}>
          <h2 {...stylex.props(styles.sectionTitle)}>About</h2>
          <p {...stylex.props(styles.text)}>
            Senior Software Engineer focused on building scalable, performant,
            and beautiful web applications. I turn complex problems into elegant
            solutions.
          </p>
        </div>

        <div {...stylex.props(styles.cell, styles.stackCell)}>
          <h2 {...stylex.props(styles.sectionTitle)}>⚡ Tech Stack</h2>
          <div {...stylex.props(styles.pillGrid)}>
            {TECH_STACKS.map((tech) => (
              <span key={tech.name} {...stylex.props(styles.pill)}>
                {tech.name}
              </span>
            ))}
          </div>
        </div>

        <div {...stylex.props(styles.cell)}>
          <div {...stylex.props(styles.iconBox)}>🚀</div>
          <h3 {...stylex.props(styles.sectionTitle)}>Performance</h3>
          <p {...stylex.props(styles.text)}>
            Optimized for speed and efficiency.
          </p>
        </div>

        <div {...stylex.props(styles.cell)}>
          <div {...stylex.props(styles.iconBox)}>🎨</div>
          <h3 {...stylex.props(styles.sectionTitle)}>Design</h3>
          <p {...stylex.props(styles.text)}>
            Pixel-perfect attention to detail.
          </p>
        </div>

        <div {...stylex.props(styles.cell, styles.projectsCell)}>
          <div {...stylex.props(styles.projectsHeader)}>
            <h2 {...stylex.props(styles.sectionTitle)}>🚀 Featured Projects</h2>
            <Link to="/project" {...stylex.props(styles.viewAllLink)}>
              View All
            </Link>
          </div>
          <div {...stylex.props(styles.projectsGrid)}>
            {PROJECTS.filter((p) => p.featured)
              .slice(0, 4)
              .map((project) => (
                <a
                  key={project.id}
                  href={project.demo || project.github}
                  target="_blank"
                  rel="noopener noreferrer"
                  {...stylex.props(styles.projectCard)}
                >
                  <div {...stylex.props(styles.projectImageContainer)}>
                    <img
                      src={project.image}
                      alt={project.title}
                      {...stylex.props(styles.projectCardImage)}
                    />
                    <div {...stylex.props(styles.projectImageOverlay)} />
                  </div>
                  <div {...stylex.props(styles.projectCardContent)}>
                    <h3 {...stylex.props(styles.projectCardTitle)}>
                      {project.title}
                    </h3>
                    <div {...stylex.props(styles.projectTechList)}>
                      {project.tech.slice(0, 3).map((tech, i) => (
                        <span key={i} {...stylex.props(styles.projectTechPill)}>
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                </a>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Home;

const shimmer = stylex.keyframes({
  "0%": { backgroundPosition: "-200% 0" },
  "100%": { backgroundPosition: "200% 0" },
});

const fadeIn = stylex.keyframes({
  "0%": { opacity: 0, transform: "translateY(20px)" },
  "100%": { opacity: 1, transform: "translateY(0)" },
});

const loadProgress = stylex.keyframes({
  "0%": { strokeDasharray: "0, 150.796" },
  "100%": { strokeDasharray: "150.796, 150.796" },
});

const styles = stylex.create({
  pageWrapper: {
    backgroundAttachment: "fixed",
    backgroundImage: "var(--bg-gradient)",
    boxSizing: "border-box",
    display: "flex",
    justifyContent: "center",
    minHeight: "100vh",
    paddingBottom: {
      default: "4rem",
      "@media (max-width: 768px)": "2rem",
    },
    paddingLeft: {
      default: "2rem",
      "@media (max-width: 768px)": "1rem",
    },
    paddingRight: {
      default: "2rem",
      "@media (max-width: 768px)": "1rem",
    },
    paddingTop: {
      default: "4rem",
      "@media (max-width: 768px)": "2rem",
    },
    width: "100%",
  },
  bentoGrid: {
    gap: "1.5rem",
    animationDuration: "0.8s",
    animationName: fadeIn,
    animationTimingFunction: "ease-out",
    display: "grid",
    gridAutoRows: "minmax(180px, auto)",
    gridTemplateColumns: {
      default: "repeat(4, 1fr)",
      "@media (max-width: 1024px)": "repeat(2, 1fr)",
      "@media (max-width: 600px)": "1fr",
    },
    maxWidth: "1200px",
    width: "100%",
  },
  cell: {
    padding: "2rem",
    borderColor: {
      default: "var(--media-inner-border)",
      ":hover": "var(--glow-border)",
    },
    borderRadius: "var(--card-corner-radius)",
    borderStyle: "solid",
    borderWidth: "1px",
    overflow: "hidden",
    transition: "all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
    backdropFilter: "blur(20px)",
    backgroundColor: "var(--surface-glass)",
    boxShadow: {
      default: "var(--shadow-sm)",
      ":hover": "var(--shadow-neon)",
    },
    cursor: "default",
    display: "flex",
    flexDirection: "column",
    position: "relative",
    transform: {
      default: "translateY(0) scale(1)",
      ":hover": "translateY(-4px) scale(1.01)",
    },
    willChange: "transform",
  },
  heroCell: {
    gridColumn: {
      default: "span 2",
      "@media (max-width: 600px)": "span 1",
    },
    gridRow: {
      default: "span 2",
      "@media (max-width: 600px)": "span 1",
    },
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
  },
  aboutCell: {
    gridColumn: {
      default: "span 2",
      "@media (max-width: 600px)": "span 1",
    },
    justifyContent: "center",
  },
  stackCell: {
    gridColumn: {
      default: "span 2",
      "@media (max-width: 600px)": "span 1",
    },
    gridRow: {
      default: "span 2",
      "@media (max-width: 600px)": "auto",
    },
  },
  profileImage: {
    borderRadius: "50%",
    backfaceVisibility: "hidden",
    objectFit: "cover",
    height: "100%",
    width: "100%",
  },
  profileRevealContainer: {
    alignItems: "center",
    cursor: "pointer",
    display: "flex",
    justifyContent: "center",
    perspective: "1000px",
    position: "relative",
    height: "140px",
    marginBottom: "1.5rem",
    width: "140px",
  },
  imageWrapper: {
    borderColor: "var(--profile-border)",
    borderRadius: "50%",
    borderStyle: "solid",
    borderWidth: "2px",
    transition: "transform 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
    boxShadow: "var(--profile-shadow)",
    position: "relative",
    transformStyle: "preserve-3d",
    height: "100%",
    width: "100%",
  },
  isFlipped: {
    transform: "rotateY(180deg)",
  },
  frontImage: {
    position: "relative",
  },
  backImage: {
    position: "absolute",
    transform: "rotateY(180deg)",
    left: 0,
    top: 0,
  },
  progressSvg: {
    pointerEvents: "none",
    position: "absolute",
    transform: "rotate(-90deg)",
    height: "160px",
    left: "-10px",
    top: "-10px",
    width: "160px",
  },
  progressCircleBack: {
    fill: "none",
    stroke: "var(--media-inner-border)",
    strokeWidth: "4px",
    opacity: 0.3,
  },
  progressCircleFront: {
    fill: "none",
    stroke: "var(--primary-accent)",
    strokeDasharray: "452.389",
    strokeLinecap: "round",
    strokeWidth: "4px",
    transition: "stroke-dashoffset 0.03s linear",
  },
  skeleton: {
    borderRadius: "50%",
    animationDuration: "1.5s",
    animationIterationCount: "infinite",
    animationName: shimmer,
    backgroundColor: "var(--media-inner-border)",
    backgroundImage:
      "linear-gradient(90deg, var(--media-inner-border) 25%, var(--surface-glass-strong) 50%, var(--media-inner-border) 75%)",
    backgroundSize: "200% 100%",
    boxShadow: "var(--profile-shadow)",
    position: "relative",
    height: "140px",
    marginBottom: "1.5rem",
    width: "140px",
  },
  loaderSvg: {
    padding: 0,
    position: "absolute",
    transform: "rotate(-90deg)",
    height: "100%",
    left: 0,
    top: 0,
    width: "100%",
  },
  loaderCircle: {
    fill: "none",
    stroke: "var(--loading-border-color)",
    strokeLinecap: "round",
    strokeWidth: "2px",
    animationDuration: "2.5s",
    animationFillMode: "forwards",
    animationIterationCount: 1,
    animationName: loadProgress,
    animationTimingFunction: "ease-in-out",
  },
  heroTitle: {
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    margin: 0,
    backgroundImage: "var(--primary-gradient)",
    fontSize: {
      default: "3.5rem",
      "@media (max-width: 768px)": "2.5rem",
    },
    fontWeight: 800,
    letterSpacing: "-0.03em",
    lineHeight: 1.1,
    marginBottom: "0.5rem",
  },
  heroSubtitle: {
    margin: 0,
    color: "var(--text-secondary)",
    fontSize: "1.25rem",
    fontWeight: 500,
  },
  sectionTitle: {
    gap: "0.5rem",
    alignItems: "center",
    color: "var(--primary-text)",
    display: "flex",
    fontSize: "1.5rem",
    fontWeight: 700,
    marginBottom: "1.5rem",
  },
  text: {
    margin: 0,
    color: "var(--text-secondary)",
    fontSize: "1.1rem",
    lineHeight: 1.6,
  },
  pillGrid: {
    gap: "0.8rem",
    display: "flex",
    flexWrap: "wrap",
  },
  pill: {
    borderColor: {
      default: "var(--media-inner-border)",
      ":hover": "var(--primary-accent)",
    },
    borderRadius: "50px",
    borderStyle: "solid",
    borderWidth: "1px",
    transition: "all 0.2s ease",
    backfaceVisibility: "hidden",
    backgroundColor: {
      default: "var(--media-inner-border)",
      ":hover": "var(--primary-accent)",
    },
    boxShadow: {
      default: null,
      ":hover": "0 4px 12px rgba(0,0,0,0.1)",
    },
    color: {
      default: "var(--primary-text)",
      ":hover": "#FFFFFF",
    },
    fontSize: "0.9rem",
    fontWeight: 600,
    willChange: "transform",
    paddingBottom: "0.6rem",
    paddingLeft: "1.2rem",
    paddingRight: "1.2rem",
    paddingTop: "0.6rem",
  },
  iconBox: {
    fontSize: "2.5rem",
    marginBottom: "0.5rem",
  },
  projectsCell: {
    gridColumn: {
      default: "span 4",
      "@media (max-width: 1024px)": "span 2",
      "@media (max-width: 600px)": "span 1",
    },
  },
  projectsHeader: {
    alignItems: "center",
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "0.5rem",
  },
  viewAllLink: {
    borderRadius: "50px",
    textDecoration: "none",
    transition: "all 0.2s ease",
    backgroundColor: {
      default: "transparent",
      ":hover": "var(--primary-accent)",
    },
    color: {
      default: "var(--primary-accent)",
      ":hover": "#FFFFFF",
    },
    fontSize: "0.9rem",
    fontWeight: 600,
    paddingBottom: "0.5rem",
    paddingLeft: "1rem",
    paddingRight: "1rem",
    paddingTop: "0.5rem",
  },
  projectsGrid: {
    gap: "1rem",
    display: "grid",
    gridTemplateColumns: {
      default: "repeat(4, 1fr)",
      "@media (max-width: 1024px)": "repeat(2, 1fr)",
      "@media (max-width: 600px)": "1fr",
    },
  },
  projectCard: {
    borderColor: {
      default: "var(--media-inner-border)",
      ":hover": "var(--glow-border)",
    },
    borderRadius: "12px",
    borderStyle: "solid",
    borderWidth: "1px",
    overflow: "hidden",
    textDecoration: "none",
    transition: "all 0.3s ease",
    backgroundColor: "var(--surface-glass)",
    boxShadow: {
      default: "none",
      ":hover": "var(--shadow-neon)",
    },
    display: "flex",
    flexDirection: "column",
    transform: {
      default: null,
      ":hover": "translateY(-4px)",
    },
  },
  projectImageContainer: {
    overflow: "hidden",
    position: "relative",
    height: "120px",
  },
  projectCardImage: {
    transition: "transform 0.3s ease",
    objectFit: "cover",
    height: "100%",
    width: "100%",
  },
  projectImageOverlay: {
    backgroundImage: "linear-gradient(to top, rgba(0, 0, 0, 0.4), transparent)",
    pointerEvents: "none",
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    top: 0,
  },
  projectCardContent: {
    padding: "0.75rem",
    gap: "0.5rem",
    display: "flex",
    flexDirection: "column",
  },
  projectCardTitle: {
    overflow: "hidden",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: 1,
    color: "var(--primary-text)",
    display: "-webkit-box",
    fontSize: "0.9rem",
    fontWeight: 600,
    marginBottom: 0,
    marginLeft: 0,
    marginRight: 0,
    marginTop: 0,
  },
  projectTechList: {
    gap: "0.35rem",
    display: "flex",
    flexWrap: "wrap",
  },
  projectTechPill: {
    borderRadius: "50px",
    backgroundColor: "var(--media-inner-border)",
    color: "var(--text-secondary)",
    fontSize: "0.65rem",
    fontWeight: 500,
    paddingBottom: "0.2rem",
    paddingLeft: "0.5rem",
    paddingRight: "0.5rem",
    paddingTop: "0.2rem",
  },
});
